import React from 'react';
import { useSelector } from 'react-redux';
import { RootState } from '../../../../store/types';
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer
} from 'recharts';
import { colorGray500, colorGray100 } from '@amzn/meridian-tokens/base/color';
import { ProjectedVolumeDataPoint } from '../../model';
import { ACTUALLY_PROCESSED_LABEL, getActualColors, mergeTimesInOrder } from '../../../../utils/midShiftActuals';
import { selectIsMidShiftReplanEnabled } from '../../../../store/slices/v2/midShiftReplanFeatureSlice';
import './Chart.scss';

interface ChartProps {
  /** RCC Recommended (baseline) projected curve — drives the black line. */
  rccSeries?: ProjectedVolumeDataPoint[];
  /** Operator Override projected curve — drives the orange dashed line. */
  overrideSeries?: ProjectedVolumeDataPoint[];
  /**
   * Mid-shift re-plan: volume already processed this shift (cumulative), drawn as the green dotted
   * "Actually processed" line. The projected line of the same plan starts at its last point.
   */
  actualSeries?: ProjectedVolumeDataPoint[];
}

// The actuals key exists only when actuals are shown, so the flag-off rows keep today's shape.
type ChartRow = {
  time: string;
  'RCC Recommended': number | null;
  'Operator Override': number | null;
  [ACTUALLY_PROCESSED_LABEL]?: number | null;
};

const tooltipNumberFormatter = new Intl.NumberFormat('en-US');

interface TooltipPayloadEntry {
  name: string;
  value: number;
  color: string;
}

/**
 * Horizontal anchor for the tooltip: centered over the point by default, flipped
 * to extend left near the right edge (and right near the left edge) so the
 * last/first point's tooltip isn't clipped by the chart card. Uses the active
 * point's pixel position (coordinate.x) relative to the plot-area box (viewBox).
 */
const getTooltipTranslateX = (
  coordinate?: { x?: number },
  viewBox?: { x?: number; width?: number }
): string => {
  if (typeof coordinate?.x !== 'number' || typeof viewBox?.x !== 'number' || !viewBox?.width) {
    return '-50%';
  }
  const relative = (coordinate.x - viewBox.x) / viewBox.width;
  if (relative > 0.75) {
    return '-100%';
  }
  if (relative < 0.25) {
    return '0';
  }
  return '-50%';
};

/**
 * Custom black tooltip (mock) — bold time header, then one colored-dot row per
 * series showing "<series>: <value> units".
 */
const ChartTooltip: React.FC<{
  active?: boolean;
  label?: string;
  payload?: TooltipPayloadEntry[];
  hoveredSeries?: string | null;
  // recharts passes the active point's pixel position and the plot-area box; used
  // to keep the tooltip from spilling past (and being clipped by) the card edges.
  coordinate?: { x?: number; y?: number };
  viewBox?: { x?: number; width?: number };
}> = ({ active, label, payload, hoveredSeries, coordinate, viewBox }) => {
  // Only show the tooltip when the cursor is directly on a series' data point
  // (not when merely hovering the chart area) — and only for that series.
  if (!active || !payload || payload.length === 0 || !hoveredSeries) {
    return null;
  }
  const rows = payload
    .filter((entry) => entry.value !== null && entry.value !== undefined)
    .filter((entry) => entry.name === hoveredSeries);

  if (rows.length === 0) {
    return null;
  }

  return (
    <div
      className="chart-v2__tooltip"
      style={{ transform: `translate(${getTooltipTranslateX(coordinate, viewBox)}, calc(-100% - 14px))` }}
    >
      <div className="chart-v2__tooltip-header">{label}</div>
      <div className="chart-v2__tooltip-body">
        {rows.map((entry) => (
          <div key={entry.name} className="chart-v2__tooltip-row">
            <span className="chart-v2__tooltip-dot" style={{ backgroundColor: entry.color }} />
            <span>{`${entry.name}: ${tooltipNumberFormatter.format(entry.value)} units`}</span>
          </div>
        ))}
      </div>
    </div>
  );
};

/**
 * Refactored (V2) RCC Recommended Volume chart — UTR-REFLEX-1163.
 *
 * Overlays two cumulative curves: a solid black "RCC Recommended" line (rccSeries,
 * the baseline plan's projected volume) and an orange dashed "Operator Override"
 * line (overrideSeries, the selected override plan's projected volume). Each line
 * is drawn only when its series has data, so an absent plan simply omits its line.
 * Sibling of Chart.tsx so V1 is untouched.
 */
const Chart: React.FC<ChartProps> = ({
  rccSeries = [],
  overrideSeries = [],
  actualSeries = []
}) => {
  const currentMode = useSelector((state: RootState) => state.theme.currentMode);
  // RCC_MID_SHIFT_REPLAN (resolved into Redux by the page): draw the green actuals line, merge the
  // series' times in shift order and use the caption that explains green. Off ⇒ today's chart.
  const showActuals = useSelector(selectIsMidShiftReplanEnabled);
  const isDark = currentMode === 'dark';

  // Which series' data point the cursor is currently over — the tooltip shows
  // only that series (orange point → Operator Override, black → RCC Recommended).
  const [hoveredSeries, setHoveredSeries] = React.useState<string | null>(null);

  // Mock palette: RCC Recommended = near-black line, Operator Override = orange.
  // Recharts is styled via inline props (not a Meridian component), so colors are
  // passed directly; Meridian gray tokens back the grid/tick where they map, and
  // the dark-mode variants stay inline since they switch on `isDark`.
  const CHART_COLORS = {
    recommended: isDark ? colorGray100 : '#0F1111',
    operatorOverride: '#D8730A',
    actual: getActualColors(isDark).line,
    grid: isDark ? 'rgba(255,255,255,0.2)' : colorGray100,
    tick: isDark ? colorGray100 : colorGray500,
    legend: isDark ? colorGray100 : '#0F1111',
  };

  // Merge the two curves into one row per time slot (each series is cumulative:
  // 0 → total across the shift). A series absent for a slot is null so its line
  // simply doesn't render there.
  const data = React.useMemo(() => {
    const byTime = new Map<string, ChartRow>();
    const upsert = (time: string) => {
      if (!byTime.has(time)) {
        byTime.set(time, showActuals
          ? { time, 'RCC Recommended': null, 'Operator Override': null, [ACTUALLY_PROCESSED_LABEL]: null }
          : { time, 'RCC Recommended': null, 'Operator Override': null });
      }
      return byTime.get(time)!;
    };
    if (showActuals) {
      actualSeries.forEach((point) => { upsert(point.time)[ACTUALLY_PROCESSED_LABEL] = point.recommended ?? null; });
    }
    rccSeries.forEach((point) => { upsert(point.time)['RCC Recommended'] = point.recommended ?? null; });
    overrideSeries.forEach((point) => { upsert(point.time)['Operator Override'] = point.recommended ?? null; });
    if (!showActuals) {
      // Today's behaviour: x-axis in insertion order.
      return Array.from(byTime.values());
    }
    // Mid-shift: series can cover different hours (actuals end at the cut-over, the overlay plan may
    // start elsewhere), so lay the times out in shift order. Bare "HH:mm" can't be sorted across
    // midnight, hence an ordered merge of the already-ordered series instead of a sort.
    const times = mergeTimesInOrder([actualSeries, rccSeries, overrideSeries].map((series) => series.map((p) => p.time)));
    return times.map((time) => byTime.get(time)!);
  }, [rccSeries, overrideSeries, actualSeries, showActuals]);

  // Each line renders only if its series has data — both present → both show; an
  // absent plan simply omits its line (RCC null → no black line; override null →
  // no orange line).
  const hasRecommended = data.some(
    (d) => d['RCC Recommended'] !== null && d['RCC Recommended'] !== undefined
  );
  const hasOperatorOverride = data.some(
    (d) => d['Operator Override'] !== null && d['Operator Override'] !== undefined
  );
  const hasActual = showActuals && data.some(
    (d) => d[ACTUALLY_PROCESSED_LABEL] !== null && d[ACTUALLY_PROCESSED_LABEL] !== undefined
  );

  // Re-run the draw animation whenever the underlying data changes (initial load,
  // Plan View switch, Version change). recharts only animates on mount/data-change,
  // so keying the chart on the data forces a fresh reveal each time it updates.
  const chartKey = React.useMemo(
    () => data
      .map((d) => `${d.time}:${d['RCC Recommended']}:${d['Operator Override']}:${d[ACTUALLY_PROCESSED_LABEL] ?? ''}`)
      .join('|'),
    [data]
  );

  return (
    <div className="chart-v2">
      <div className="chart-v2__canvas">
        <ResponsiveContainer width="100%" height="100%">
          <LineChart key={chartKey} data={data} margin={{ top: 40, right: 30, bottom: 20 }}>
            <CartesianGrid strokeDasharray="1 1" stroke={CHART_COLORS.grid} vertical={false} />
            <XAxis
              dataKey="time"
              axisLine={false}
              tickLine={false}
              tick={{ fontSize: 12, fill: CHART_COLORS.tick }}
            />
            <YAxis
              axisLine={false}
              tickLine={false}
              tick={{ fontSize: 12, fill: CHART_COLORS.tick }}
              domain={[0, 'auto']}
            />
            <Tooltip
              content={<ChartTooltip hoveredSeries={hoveredSeries} />}
              cursor={false}
              isAnimationActive={false}
              offset={0}
              allowEscapeViewBox={{ x: true, y: true }}
            />

            {/* RCC Recommended — solid black cumulative line (only if data exists) */}
            {hasRecommended && (
              <Line
                type="monotone"
                dataKey="RCC Recommended"
                stroke={CHART_COLORS.recommended}
                strokeWidth={3}
                dot={{ fill: CHART_COLORS.recommended, r: 4 }}
                activeDot={{
                  r: 6,
                  fill: CHART_COLORS.recommended,
                  onMouseEnter: () => setHoveredSeries('RCC Recommended'),
                  onMouseLeave: () => setHoveredSeries(null),
                }}
                connectNulls={false}
              />
            )}

            {/* Operator Override — orange dashed line */}
            {hasOperatorOverride && (
              <Line
                type="monotone"
                dataKey="Operator Override"
                stroke={CHART_COLORS.operatorOverride}
                strokeWidth={2}
                strokeDasharray="6 6"
                // Dots need a solid stroke-dasharray, else they inherit the line's
                // dashed pattern and the circle outline renders broken/non-round.
                dot={{ fill: CHART_COLORS.operatorOverride, r: 4, strokeDasharray: '0' }}
                activeDot={{
                  r: 6,
                  fill: CHART_COLORS.operatorOverride,
                  strokeDasharray: '0',
                  onMouseEnter: () => setHoveredSeries('Operator Override'),
                  onMouseLeave: () => setHoveredSeries(null),
                }}
                connectNulls={false}
              />
            )}

            {/* Actually processed — green dotted line (mid-shift only). Drawn last so its dot sits on
                top of the projected line's first dot where the two meet at the cut-over. */}
            {hasActual && (
              <Line
                type="monotone"
                dataKey={ACTUALLY_PROCESSED_LABEL}
                stroke={CHART_COLORS.actual}
                strokeWidth={2}
                strokeDasharray="2 4"
                dot={{ fill: CHART_COLORS.actual, r: 4, strokeDasharray: '0' }}
                activeDot={{
                  r: 6,
                  fill: CHART_COLORS.actual,
                  strokeDasharray: '0',
                  onMouseEnter: () => setHoveredSeries(ACTUALLY_PROCESSED_LABEL),
                  onMouseLeave: () => setHoveredSeries(null),
                }}
                connectNulls={false}
              />
            )}

            <Legend
              verticalAlign="bottom"
              iconType="circle"
              formatter={(value: string) => (
                // color is theme-dependent so it stays inline; static styling
                // (font-size, legend padding) lives in Chart.scss.
                <span className="chart-v2__legend-label" style={{ color: CHART_COLORS.legend }}>{value}</span>
              )}
            />
          </LineChart>
        </ResponsiveContainer>
      </div>
      {/* Mention green only when the green line is actually drawn (flag on AND the plan has actuals). */}
      {hasActual ? (
        <p className="chart-v2__caption">
          Hover over data points to see values.
          {hasOperatorOverride &&
            ' Orange (Operator Override) shows manual adjustments against the RCC Recommended volume.'}
          {' '}Green (Actually processed) shows primary grading volume processed so far this shift.
        </p>
      ) : (
        <p className="chart-v2__caption">
          Hover over data points to see values. Operator Override shows manual adjustments
          against the RCC Recommended volume.
        </p>
      )}
    </div>
  );
};

export default Chart;

import './Chart.scss';